import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { API } from '@shared/types/api';
import {
  useCreateSharedLink,
  useListSharedCardLinks,
  useRemoveSharedCardLink,
} from '../../channel/options/hooks';
import { useShareQrCode } from './useShareQrCode';

export function useHandleCopyQr() {
  const copy = useShareQrCode();
  return useCallback(
    qrCode => {
      copy(qrCode);
    },
    [copy],
  );
}

interface FindSharedLink {
  type: API.SharedCardLinkType;
  cardId?: string | null;
  viewTemplateId?: string | null;
}
export function findExistingSharedLink(
  options: FindSharedLink,
  allLinks: API.SharedCardLinkFragment[] | undefined,
): API.SharedCardLinkFragment | undefined {
  return allLinks?.find(
    existingLink =>
      existingLink.type === options.type &&
      (existingLink.cardId === options.cardId ||
        (isNil(options.cardId) && existingLink.viewTemplateId === options.viewTemplateId)),
  );
}

interface SharedCardLinkContext {
  channelId: number | undefined;
  type: API.SharedCardLinkType;
  cardId?: string;
  viewTemplateId?: string;
  disabled: boolean;
}

interface PublicLink {
  id: string;
  url: string;
}

export function useSharedCardLinkState(options: SharedCardLinkContext) {
  const createShareLink = useCreateSharedLink(
    {
      type: options.type,
      channelId: options.channelId,
      cardId: options.cardId,
      viewTemplateId: options.viewTemplateId,
    },
    options.disabled,
  );

  const [publicLink, setPublicLink] = useState<PublicLink>();
  const [showPublicQrCode, setShowPublicQrCode] = useState(false);

  const sharedLinks = useListSharedCardLinks(options.channelId);

  const link = findExistingSharedLink(
    { type: options.type, cardId: options.cardId, viewTemplateId: options.viewTemplateId },
    sharedLinks?.data ?? [],
  );
  useEffect(() => {
    if (link?.id && link?.shortUrl) {
      setPublicLink({ url: link.shortUrl, id: link.id });
      setShowPublicQrCode(true);
    }
  }, [link]);

  const removeSharedLink = useRemoveSharedCardLink();

  const onToggleShowPublicQrCode = useCallback(
    (toggledOn: boolean) => {
      setShowPublicQrCode(toggledOn);
      if (toggledOn && isEmpty(publicLink)) {
        createShareLink().then(
          fulfilled => {
            setPublicLink(fulfilled);
          },
          _rejected => {},
        );
      } else if (!toggledOn && !isNil(publicLink)) {
        removeSharedLink(publicLink.id).finally(() => {
          setPublicLink(undefined);
          sharedLinks?.refetch();
        });
      }
    },
    [createShareLink, publicLink, removeSharedLink, sharedLinks],
  );
  return { showPublicQrCode, publicLink, onToggleShowPublicQrCode };
}
